// src/pages/AppointmentPage.js
import React, { useState, useEffect } from "react";

const API_BASE = "https://brightlifephysio.com/api";

const AppointmentPage = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedService, setSelectedService] = useState("Physiotherapy");

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [unavailableSlots, setUnavailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // ---------------------------------------------------------------------------
  // Load slots whenever DATE changes (service does NOT matter)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDate) {
        setSlots([]);
        setUnavailableSlots([]);
        return;
      }

      try {
        setLoadingSlots(true);

        const params = new URLSearchParams({ date: selectedDate });

        const res = await fetch(`${API_BASE}/get-slots.php?${params.toString()}`);
        const text = await res.text();
        console.log("get-slots raw response:", text);

        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          setErrorMessage("Server returned invalid response.");
          setSlots([]);
          setUnavailableSlots([]);
          return;
        }

        if (data.success) {
          const loadedSlots = data.slots || [];
          setSlots(loadedSlots);

          const blocked = loadedSlots
            .filter((s) => s.available === false)
            .map((s) => s.time);

          setUnavailableSlots(blocked);
          setErrorMessage("");
        } else {
          setErrorMessage(data.message || "Unable to load slots.");
          setSlots([]);
          setUnavailableSlots([]);
        }
      } catch (err) {
        console.error("fetchSlots error:", err);
        setErrorMessage("Failed to load time slots.");
        setSlots([]);
        setUnavailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [selectedDate]);

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!selectedSlot) {
      setErrorMessage("Please select a time slot.");
      return;
    }

    const form = e.target;
    const formData = new FormData(form);

    try {
      setSubmitting(true);

      const res = await fetch(`${API_BASE}/book-appointment.php`, {
        method: "POST",
        body: formData,
      });

      const text = await res.text();
      console.log("Book raw response:", text);

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        setErrorMessage("Server response: " + text);
        return;
      }

      console.log("Book response (JSON):", data);

      if (data.success) {
        setSuccessMessage("Your appointment has been booked successfully.");

        // Hard refresh after a short delay so ALL state + form is reset
        setTimeout(() => {
          window.location.reload();
        }, 1000);

        return;
      }

      if (data.code === "slot_full") {
        setErrorMessage("This time slot is already full. Please choose another.");
      } else {
        setErrorMessage(data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("handleSubmit error:", err);
      setErrorMessage("Network error: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="appointment-page section">
      <div className="container">
        <h2 className="section-title">Book Appointment</h2>
        <p className="section-subtitle">
          Choose your preferred date, service, and available time slot.
        </p>

        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}
        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}

        <form
          className="ap-form"
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          autoComplete="off"
        >
          <div className="ap-grid">
            {/* Name */}
            <div className="field">
              <input type="text" name="name" required placeholder=" " />
              <label>Full Name</label>
            </div>

            {/* Email */}
            <div className="field">
              <input type="email" name="email" placeholder=" " />
              <label>Email (optional)</label>
            </div>

            {/* Phone */}
            <div className="field">
              <input type="tel" name="phone" required placeholder=" " />
              <label>Phone</label>
            </div>

            {/* Service - only for info, not for slot capacity */}
            <div className="field">
              <select
                name="service"
                required
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
              >
                <option>Physiotherapy</option>
                <option>Sports Rehab</option>
                <option>Osteopathy</option>
                <option>Acupuncture & Dry Needling</option>
                <option>Craniosacral Therapy</option>
                <option>Matrix Rhythm Therapy</option>
                <option>Pelvic Floor Rehabilitation</option>
              </select>
              <label>Service</label>
            </div>

            {/* Date */}
            <div className="field">
              <input
                type="date"
                name="date"
                required
                onChange={(e) => setSelectedDate(e.target.value)}
              />
              <label>Date</label>
            </div>

            {/* Hidden input to send the chosen time slot to PHP */}
            <input type="hidden" name="slot" value={selectedSlot} />

            {/* Time Slot Buttons */}
            <div className="field field-full">
              <label style={{ display: "block", marginBottom: "6px" }}>
                Time Slot
              </label>

              {loadingSlots && <p>Loading available slots...</p>}

              {!loadingSlots && slots.length === 0 && selectedDate && (
                <p>No slots defined for this day.</p>
              )}

              <div className="slot-grid">
                {slots.map((slot) => {
                  const isBlocked = unavailableSlots.includes(slot.time);
                  const isSelected = selectedSlot === slot.time;
                  const left =
                    typeof slot.left === "number"
                      ? slot.left
                      : Math.max(6 - (slot.taken || 0), 0); // fallback

                  return (
                    <button
                      type="button"
                      key={slot.time}
                      className={`slot-btn ${
                        isBlocked ? "slot-blocked" : ""
                      } ${isSelected ? "slot-active" : ""}`}
                      disabled={isBlocked}
                      onClick={() => setSelectedSlot(slot.time)}
                    >
                      <span className="slot-time">{slot.time}</span>
                      <span className="slot-left">{left} left</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div className="field field-full">
              <textarea name="notes" rows="3" placeholder=" "></textarea>
              <label>Notes (optional)</label>
            </div>
          </div>

          <button type="submit" className="btn-default" disabled={submitting}>
            {submitting ? "Booking..." : "Confirm Appointment"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AppointmentPage;
